import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicId } from "@/lib/utils/clinic-context";
import { CURRENT_CLINIC_ID_QUERY_KEY } from "@/lib/react-query/queryKeys";

export type AppointmentTypeBehavior = "padrao" | "retorno" | "aplicacao" | "banho_tosa";

export type ClinicAppointmentType = {
  id: string;
  clinic_id: string;
  name: string;
  color: string;
  icon: string;
  behavior: AppointmentTypeBehavior;
  order: number;
  active: boolean;
  created_at: string;
};

export type ClinicAppointmentTypeInsert = {
  name: string;
  color: string;
  icon: string;
  behavior: AppointmentTypeBehavior;
  order?: number;
  active?: boolean;
};

export const CLINIC_APPOINTMENT_TYPES_QUERY_KEY = "clinic_appointment_types";

export function useClinicAppointmentTypes() {
  return useQuery({
    queryKey: [CLINIC_APPOINTMENT_TYPES_QUERY_KEY, CURRENT_CLINIC_ID_QUERY_KEY],
    queryFn: async () => {
      const clinicId = await getCurrentClinicId();
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("clinic_appointment_types")
        .select("*")
        .eq("clinic_id", clinicId)
        .eq("active", true)
        .order("order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ClinicAppointmentType[];
    },
  });
}

export function useAllClinicAppointmentTypes() {
  return useQuery({
    queryKey: [CLINIC_APPOINTMENT_TYPES_QUERY_KEY, "all", CURRENT_CLINIC_ID_QUERY_KEY],
    queryFn: async () => {
      const clinicId = await getCurrentClinicId();
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("clinic_appointment_types")
        .select("*")
        .eq("clinic_id", clinicId)
        .order("order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ClinicAppointmentType[];
    },
  });
}

export function useCreateClinicAppointmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ClinicAppointmentTypeInsert) => {
      const clinicId = await getCurrentClinicId();
      if (!clinicId) throw new Error("No clinic selected");
      const { data, error } = await supabase
        .from("clinic_appointment_types")
        .insert({ ...payload, clinic_id: clinicId })
        .select()
        .single();
      if (error) throw error;
      return data as ClinicAppointmentType;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLINIC_APPOINTMENT_TYPES_QUERY_KEY] });
    },
  });
}

export function useUpdateClinicAppointmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ClinicAppointmentTypeInsert> & { id: string }) => {
      const { data, error } = await supabase
        .from("clinic_appointment_types")
        .update(payload)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as ClinicAppointmentType;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CLINIC_APPOINTMENT_TYPES_QUERY_KEY] });
    },
  });
}

export function useTypeIdsByBehavior(behavior: AppointmentTypeBehavior): string[] {
  const { data: types = [] } = useClinicAppointmentTypes();
  return useMemo(
    () => types.filter((t) => t.behavior === behavior).map((t) => t.id),
    [types, behavior],
  );
}
